import React from 'react';
import { baseURL } from '../../api/baseURL';
import axios from 'axios';
import { connect } from 'react-redux';
import Loader from '../../Loader';
import Modal from '../../Modal';
import AdminShell from '../layout/AdminShell';
import { countPillStyle, thStyle } from '../layout/listUi';
import { getSnapwizMediaOrigin } from '../../api/hostport';

function mediaAbsoluteUrl(path?: string | null): string | undefined {
    if (!path) return undefined;
    const p = String(path).trim();
    if (!p) return undefined;
    if (/^https?:\/\//i.test(p)) return p;
    const base = getSnapwizMediaOrigin().replace(/\/+$/, '');
    return `${base}${p.startsWith('/') ? '' : '/'}${p}`;
}

type PendingRow = {
    _id: string;
    name: string;
    email: string;
    contact: string;
    requested_name?: string | null;
    requested_contact?: string | null;
    requested_email?: string | null;
    phoneModel?: string | null;
    requested_phoneModel?: string | null;
    gimble?: string | null;
    requested_gimble?: string | null;
    bank_details?: { bank_holder?: string; acc_no?: string; ifsc_code?: string; upi_id?: string } | null;
    requested_bank_details?: { bank_holder?: string; acc_no?: string; ifsc_code?: string; upi_id?: string } | null;
    cameras_owned?: string[];
    requested_cameras_owned?: string[] | null;
    lens_owned?: string[];
    requested_lens_owned?: string[] | null;
    flash_owned?: string[];
    requested_flash_owned?: string[] | null;
    addl_equipments?: string | null;
    requested_addl_equipments?: string | null;
    profile_pic?: string | null;
    requested_profile_pic?: string | null;
    govt_id_pic?: string | null;
    requested_govt_id_pic?: string | null;
    instagram?: string | null;
    requested_instagram?: string | null;
    facebook?: string | null;
    requested_facebook?: string | null;
    address?: string | null;
    requested_address?: string | null;
    location?: { lat?: number; lng?: number } | null;
    requested_location?: { lat?: number; lng?: number } | null;
    profile_edit_requested_at?: string | null;
    specialization?: string[];
    requested_specialization?: string[];
    specialization_requested_at?: string | null;
};

type State = {
    requested: boolean;
    err: boolean;
    requests: PendingRow[];
    actioningId: string | null;
    highlightId: string | null;
};

/** Photographers can request a Name/Contact/Email change, or ask for
 * additional service specializations, but neither takes effect until an
 * admin approves it here. Service-request approval reuses the existing
 * Photographers page's "Admin-assigned shoot roles" save action (checking
 * the requested boxes there IS the approval) — this page surfaces who's
 * asking, and handles approve/reject for profile edits plus a plain reject
 * for service requests. */
class PendingRequestsPage extends React.Component<{ history: any; token: string }> {
    state: State = {
        requested: false,
        err: false,
        requests: [],
        actioningId: null,
        highlightId: null,
    };

    componentDidMount(): void {
        if (!this.props.token) {
            window.location.pathname = '/';
        } else {
            // Client-reported: the photographer detail page's "Review on
            // Pending Requests" link dumped admin into the full undifferentiated
            // list — deep-link straight to that photographer's own row instead.
            const params = new URLSearchParams(window.location.search);
            const photographerId = params.get('photographerId');
            if (photographerId) this.setState({ highlightId: photographerId });
            this.getList();
        }
    }

    getList = () => {
        if (this.state.requested) return;
        this.setState({ err: false, requested: true });
        axios
            .get(`${baseURL}/pending-requests`, {
                headers: { Authorization: `Bearer ${this.props.token}` },
            })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ requests: response?.data?.requests || [], requested: false, err: false }, () => {
                        if (this.state.highlightId) {
                            const el = document.getElementById(`pending-row-${this.state.highlightId}`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });
                } else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => {
                this.setState({ err: true, requested: false });
            });
    };

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }
    };

    act = (path: string, userId: string) => {
        if (this.state.actioningId) return;
        this.setState({ actioningId: userId });
        axios
            .post(
                `${baseURL}${path}`,
                { userId },
                { headers: { Authorization: `Bearer ${this.props.token}` } },
            )
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ actioningId: null }, () => this.getList());
                } else {
                    this.setState({ err: true, actioningId: null });
                }
            })
            .catch(() => {
                this.setState({ err: true, actioningId: null });
            });
    };

    render() {
        const profileEditRows = this.state.requests.filter((r) => r.profile_edit_requested_at);
        const serviceRequestRows = this.state.requests.filter((r) => r.specialization_requested_at);

        return (
            <AdminShell activePage="pending-requests" title="Pending Requests" subtitle="Photographer profile-edit and service requests awaiting approval">
                <Modal
                    open={this.state.err}
                    hideSec
                    primaryText="Okay"
                    title="Update"
                    onConfirm={this.toggleModal}
                    toggleOpen={this.toggleModal}
                    content={<h6>Something went wrong, please try again later</h6>}
                />
                {this.state.requested ? <Loader /> : null}
                <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-body">
                        <h4 className="card-title">
                            Profile Edit Requests
                            {profileEditRows.length ? <span style={countPillStyle}>{profileEditRows.length}</span> : null}
                        </h4>
                        <p style={{ color: '#888', marginTop: 8 }}>
                            Photographer-submitted Name/Contact/Email/iPhone-model/Bank-details
                            changes — nothing takes effect until approved here.
                        </p>
                        <div className="table-responsive">
                            {profileEditRows.length ? (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Current</th>
                                            <th style={thStyle}>Requested</th>
                                            <th style={thStyle}>Requested On</th>
                                            <th style={thStyle}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {profileEditRows.map((item) => (
                                            <tr
                                                key={item._id}
                                                id={`pending-row-${item._id}`}
                                                style={this.state.highlightId === item._id ? { outline: '2px solid #29B6F7', background: 'rgba(41,182,247,0.08)' } : undefined}
                                            >
                                                <td>
                                                    <div>{item.name || '—'}</div>
                                                    <div style={{ color: '#888', fontSize: 12 }}>{item.contact || '—'}</div>
                                                    <div style={{ color: '#888', fontSize: 12 }}>{item.email || '—'}</div>
                                                    {item.requested_phoneModel ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>iPhone: {item.phoneModel || '—'}</div>
                                                    ) : null}
                                                    {item.requested_gimble ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Gimbal: {item.gimble || '—'}</div>
                                                    ) : null}
                                                    {item.requested_bank_details?.acc_no ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>
                                                            Bank: {item.bank_details?.bank_holder || '—'} · {item.bank_details?.acc_no || '—'} · {item.bank_details?.ifsc_code || '—'}
                                                        </div>
                                                    ) : null}
                                                    {item.requested_cameras_owned ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Cameras: {(item.cameras_owned || []).join(', ') || '—'}</div>
                                                    ) : null}
                                                    {item.requested_lens_owned ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Lens: {(item.lens_owned || []).join(', ') || '—'}</div>
                                                    ) : null}
                                                    {item.requested_flash_owned ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Flash: {(item.flash_owned || []).join(', ') || '—'}</div>
                                                    ) : null}
                                                    {item.requested_addl_equipments != null ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Other equipment: {item.addl_equipments || '—'}</div>
                                                    ) : null}
                                                    {item.requested_instagram != null ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Insta: {item.instagram || '—'}</div>
                                                    ) : null}
                                                    {item.requested_facebook != null ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Facebook: {item.facebook || '—'}</div>
                                                    ) : null}
                                                    {item.requested_address != null ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Address: {item.address || '—'}</div>
                                                    ) : null}
                                                    {item.requested_location ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Location: {item.location?.lat != null ? `${item.location.lat}, ${item.location.lng}` : '—'}</div>
                                                    ) : null}
                                                    {item.requested_profile_pic ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Profile photo: <a href={mediaAbsoluteUrl(item.profile_pic)} target="_blank" rel="noopener noreferrer" style={{ color: '#8ED4FF' }}>current</a></div>
                                                    ) : null}
                                                    {item.requested_govt_id_pic ? (
                                                        <div style={{ color: '#888', fontSize: 12 }}>Govt ID photo: <a href={mediaAbsoluteUrl(item.govt_id_pic)} target="_blank" rel="noopener noreferrer" style={{ color: '#8ED4FF' }}>current</a></div>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {item.requested_name ? <div>{item.requested_name}</div> : null}
                                                    {item.requested_contact ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>{item.requested_contact}</div>
                                                    ) : null}
                                                    {item.requested_email ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>{item.requested_email}</div>
                                                    ) : null}
                                                    {item.requested_phoneModel ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>iPhone: {item.requested_phoneModel}</div>
                                                    ) : null}
                                                    {item.requested_gimble ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Gimbal: {item.requested_gimble}</div>
                                                    ) : null}
                                                    {item.requested_bank_details?.acc_no ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>
                                                            Bank: {item.requested_bank_details.bank_holder || '—'} · {item.requested_bank_details.acc_no} · {item.requested_bank_details.ifsc_code || '—'}
                                                        </div>
                                                    ) : null}
                                                    {item.requested_cameras_owned ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Cameras: {item.requested_cameras_owned.join(', ') || '—'}</div>
                                                    ) : null}
                                                    {item.requested_lens_owned ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Lens: {item.requested_lens_owned.join(', ') || '—'}</div>
                                                    ) : null}
                                                    {item.requested_flash_owned ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Flash: {item.requested_flash_owned.join(', ') || '—'}</div>
                                                    ) : null}
                                                    {item.requested_addl_equipments != null ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Other equipment: {item.requested_addl_equipments || '—'}</div>
                                                    ) : null}
                                                    {item.requested_instagram != null ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Insta: {item.requested_instagram || '—'}</div>
                                                    ) : null}
                                                    {item.requested_facebook != null ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Facebook: {item.requested_facebook || '—'}</div>
                                                    ) : null}
                                                    {item.requested_address != null ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Address: {item.requested_address || '—'}</div>
                                                    ) : null}
                                                    {item.requested_location ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Location: {item.requested_location.lat != null ? `${item.requested_location.lat}, ${item.requested_location.lng}` : '—'}</div>
                                                    ) : null}
                                                    {item.requested_profile_pic ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Profile photo: <a href={mediaAbsoluteUrl(item.requested_profile_pic)} target="_blank" rel="noopener noreferrer" style={{ color: '#8ED4FF' }}>new photo</a></div>
                                                    ) : null}
                                                    {item.requested_govt_id_pic ? (
                                                        <div style={{ color: '#2e7d32', fontSize: 12 }}>Govt ID photo: <a href={mediaAbsoluteUrl(item.requested_govt_id_pic)} target="_blank" rel="noopener noreferrer" style={{ color: '#8ED4FF' }}>new photo</a></div>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {item.profile_edit_requested_at
                                                        ? new Date(item.profile_edit_requested_at).toLocaleString()
                                                        : '—'}
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-success btn-sm"
                                                        style={{ marginRight: 8 }}
                                                        disabled={this.state.actioningId === item._id}
                                                        onClick={() => this.act('/approve-profile-edit', item._id)}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        disabled={this.state.actioningId === item._id}
                                                        onClick={() => this.act('/reject-profile-edit', item._id)}
                                                    >
                                                        Reject
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                    <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No Data</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title">
                            Additional Service Requests
                            {serviceRequestRows.length ? <span style={countPillStyle}>{serviceRequestRows.length}</span> : null}
                        </h4>
                        <p style={{ color: '#888', marginTop: 8 }}>
                            To approve, open the photographer on the Photographers page and save
                            the "Admin-assigned shoot roles" with the requested services checked
                            — that clears the request automatically. Reject here to dismiss it
                            without changing their current services.
                        </p>
                        <div className="table-responsive">
                            {serviceRequestRows.length ? (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Photographer</th>
                                            <th style={thStyle}>Currently Approved</th>
                                            <th style={thStyle}>Requesting</th>
                                            <th style={thStyle}>Requested On</th>
                                            <th style={thStyle}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serviceRequestRows.map((item) => (
                                            <tr
                                                key={item._id}
                                                id={`pending-row-${item._id}`}
                                                style={this.state.highlightId === item._id ? { outline: '2px solid #29B6F7', background: 'rgba(41,182,247,0.08)' } : undefined}
                                            >
                                                <td>
                                                    <div>{item.name || '—'}</div>
                                                    <div style={{ color: '#888', fontSize: 12 }}>{item.email || '—'}</div>
                                                </td>
                                                <td>{(item.specialization || []).join(', ') || '—'}</td>
                                                <td style={{ color: '#2e7d32' }}>
                                                    {(item.requested_specialization || []).join(', ') || '—'}
                                                </td>
                                                <td>
                                                    {item.specialization_requested_at
                                                        ? new Date(item.specialization_requested_at).toLocaleString()
                                                        : '—'}
                                                </td>
                                                <td>
                                                    <a
                                                        href={`/photographers?photographerId=${item._id}`}
                                                        className="btn btn-success btn-sm"
                                                        style={{ marginRight: 8 }}
                                                    >
                                                        Review on Photographers page
                                                    </a>
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        disabled={this.state.actioningId === item._id}
                                                        onClick={() => this.act('/reject-service-request', item._id)}
                                                    >
                                                        Reject
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                    <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No Data</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </AdminShell>
        );
    }
}

const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch,
    };
};

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        token: state.auth.token,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PendingRequestsPage);
